/**
 * Herkennen van Supabase-storageverwijzingen (issue #282).
 *
 * De app heeft jarenlang `getPublicUrl()` gebruikt en de UITKOMST daarvan — een absolute
 * URL van de vorm `<SUPABASE_URL>/storage/v1/object/public/<bucket>/<pad>` — op tientallen
 * plekken in de database opgeslagen (meerwerk_fotos.foto_url, opnames.pdf_url,
 * oa_damage_report_fotos.foto_url, ...). Andere kolommen bevatten juist een KAAL PAD.
 * Weer andere kolommen bevatten een externe URL of een data-URI.
 *
 * Zodra een bucket op `public = false` gaat, werkt zo'n opgeslagen publieke URL niet meer.
 * Een database-backfill is echter niet nodig: uit die URL zijn bucket en pad gewoon terug te
 * lezen. Deze module doet dat, zodat alle leesplekken via één helper opnieuw kunnen tekenen
 * (`signen`) ongeacht welke van de drie vormen er in de kolom staat.
 *
 * Deze module is bewust vrij van imports (geen supabase-client), zodat de parser
 * unit-testbaar is zonder browser- of env-context.
 */

export type StorageRef = { bucket: string; path: string };

/** Waarde die niet naar storage verwijst en ongewijzigd doorgegeven moet worden. */
export type StoragePassthrough = { passthrough: string };

export type ResolvedStorageRef = StorageRef | StoragePassthrough;

export function isPassthrough(value: ResolvedStorageRef | null): value is StoragePassthrough {
  return value !== null && 'passthrough' in value;
}

/**
 * Alles wat al een volledige locatie is en dus niet meer getekend hoeft te worden:
 * externe URL's, inline data-URI's en object-URL's uit `URL.createObjectURL`.
 */
const ABSOLUTE_PATTERN = /^(https?:|data:|blob:)/i;

/**
 * Het storage-deel van een Supabase-URL:
 *   /storage/v1/object/public/<bucket>/<pad>
 *   /storage/v1/object/sign/<bucket>/<pad>?token=...
 *   /storage/v1/object/authenticated/<bucket>/<pad>
 *   /storage/v1/render/image/public/<bucket>/<pad>?width=...
 *
 * Het hostgedeelte wordt bewust NIET gecontroleerd: de projectd-URL verschilt per omgeving
 * (lokaal, preview-branch, productie) en historische records kunnen van een oudere host
 * komen. Het pad-patroon is specifiek genoeg om geen willekeurige URL te vangen.
 */
const STORAGE_URL_PATTERN =
  /\/storage\/v1\/(?:object|render\/image)\/(?:public|sign|authenticated)\/([^/?#]+)\/([^?#]+)/i;

/** Haalt percent-encoding uit een pad, zonder te klappen op een ongeldige sequentie. */
function decodePath(raw: string): string {
  return raw
    .split('/')
    .map((segment) => {
      try {
        return decodeURIComponent(segment);
      } catch {
        return segment;
      }
    })
    .join('/');
}

/** Verwijdert leidende slashes; Supabase accepteert geen pad dat met `/` begint. */
export function normalizeStoragePath(path: string): string {
  return path.replace(/^\/+/, '');
}

/**
 * Parseert een absolute Supabase-storage-URL terug naar bucket + pad.
 * Geeft `null` voor alles wat geen storage-URL is (externe link, data-URI, kaal pad).
 */
export function parseStorageUrl(value: string | null | undefined): StorageRef | null {
  if (!value) return null;
  const match = STORAGE_URL_PATTERN.exec(value);
  if (!match) return null;

  const bucket = decodePath(match[1]);
  const path = normalizeStoragePath(decodePath(match[2]));
  if (!bucket || !path) return null;

  return { bucket, path };
}

/**
 * Bepaalt wat er getekend moet worden. Accepteert twee aanroepvormen:
 *
 *   resolveStorageRef('photos', 'project-uuid/foto.jpg')   -> { bucket, path }
 *   resolveStorageRef('https://…/object/public/photos/x')  -> { bucket, path }
 *
 * en alle mengvormen daarvan, want in de praktijk staat er in `photo.file_path` soms een
 * kaal pad en soms een volledige URL. Een URL in het pad-argument WINT van het
 * bucket-argument: de bucket in de opgeslagen URL is de waarheid.
 *
 * Geeft `{ passthrough }` terug voor externe URL's / data-URI's (ongewijzigd tonen) en
 * `null` als er niets te tonen valt.
 */
export function resolveStorageRef(
  bucketOrUrl: string | null | undefined,
  path?: string | null,
): ResolvedStorageRef | null {
  // Eén-argumentvorm: de aanroeper heeft alleen een (mogelijk absolute) waarde.
  if (path === undefined) {
    if (!bucketOrUrl) return null;
    const parsed = parseStorageUrl(bucketOrUrl);
    if (parsed) return parsed;
    // Geen storage-URL: alleen zinvol als het al een volledige locatie is. Een kaal pad
    // zonder bucket is niet te tekenen.
    if (ABSOLUTE_PATTERN.test(bucketOrUrl)) return { passthrough: bucketOrUrl };
    return null;
  }

  if (!path) return null;

  const parsed = parseStorageUrl(path);
  if (parsed) return parsed;

  if (ABSOLUTE_PATTERN.test(path)) return { passthrough: path };

  if (!bucketOrUrl) return null;
  return { bucket: bucketOrUrl, path: normalizeStoragePath(path) };
}
